import { MEMBERSHIP_TIMING, RTC_PROTOCOL_V1 } from '../protocol/constants.js';
import { RtcError, silentLogger, systemClock, toRtcError, } from '../protocol/types.js';
import { memberIdFor, serverNameOf } from '../identity/identity.js';
import { isWebRtcBlocked, probeIceGathering } from '../diagnostics/iceProbe.js';
import { KeyBridge } from '../keys/KeyBridge.js';
import { LivekitController } from '../livekit/LivekitController.js';
import { classifyDeviceError, deviceKindOf, withDeviceIssue } from '../livekit/deviceIssues.js';
import { SignalChannel } from '../signals/SignalChannel.js';
import { canTransition, initialGroupCallState, isActivePhase, mergeParticipants, summarizeEncryption, } from '../state/groupCallState.js';
import { TypedEmitter, ValueStore } from '../util/emitter.js';
import { randomRequestId } from '../util/requestId.js';
export class GroupCallController {
    constructor(deps) {
        this.deps = deps;
        this.state = new ValueStore(initialGroupCallState);
        this.emitter = new TypedEmitter();
        this.detachers = [];
        this.knownIdentities = new Set();
        this.rejoinAttempted = false;
        this.signallingRecoveries = 0;
        this.lastSignallingRecoveryAt = 0;
        this.generation = 0;
        this.logger = deps.logger ?? silentLogger;
        this.clock = deps.clock ?? systemClock;
        this.maxParticipants = deps.maxParticipants ?? RTC_PROTOCOL_V1.maxParticipants;
        this.store = this.state;
        this.signals = new SignalChannel(identity => this.knownIdentities.has(identity), this.clock);
        this.signals.on('reaction', reaction => this.emitter.emit('reaction', reaction));
        this.signals.on('hands', () => this.recompute());
    }
    get room() {
        return this.livekit?.room;
    }
    get roomId() {
        return this.state.getSnapshot().roomId;
    }
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    async prepare(roomId, intent) {
        const current = this.state.getSnapshot();
        if (isActivePhase(current.phase)) {
            throw new RtcError('invalid_request', 'A call is already active');
        }
        this.resetSession();
        if (this.deps.isRoomEncrypted && !this.deps.isRoomEncrypted(roomId)) {
            this.fail(new RtcError('not_encrypted', 'Calls are only allowed in encrypted rooms'), roomId);
            return;
        }
        const signalling = this.deps.signallingFactory(roomId);
        this.signalling = signalling;
        this.detachers.push(signalling.on('participants', participants => this.onMatrixParticipants(participants)));
        const lobbyParticipants = signalling.participants();
        this.state.set({
            ...initialGroupCallState,
            phase: 'lobby',
            roomId,
            intent: signalling.consensusIntent() ?? intent,
            lobbyParticipants,
            full: this.isFull(lobbyParticipants),
            local: { microphoneEnabled: true, cameraEnabled: intent === 'video', screenSharing: false },
        });
    }
    async join(options) {
        const current = this.state.getSnapshot();
        const roomId = current.roomId;
        const signalling = this.signalling;
        if (!roomId || !signalling || current.phase !== 'lobby') {
            throw new RtcError('invalid_request', 'prepare() must be called before join()');
        }
        if (this.isFull(signalling.participants())) {
            this.fail(new RtcError('call_full', 'The call has reached its participant limit'), roomId);
            return;
        }
        const generation = ++this.generation;
        const intent = options.intent ?? current.intent;
        this.transition('joining', { intent, error: undefined });
        try {
            const transport = await this.deps.tokens.discoverTransport();
            this.assertGeneration(generation);
            this.transport = transport;
            const token = await this.deps.tokens.getToken({
                roomId,
                slotId: RTC_PROTOCOL_V1.slotId,
                memberId: memberIdFor(signalling.localUserId, signalling.localDeviceId),
                url: transport.livekit_service_url,
                serverName: serverNameOf(signalling.localUserId),
            });
            this.assertGeneration(generation);
            const e2ee = this.deps.platform.createE2EE();
            const keyBridge = new KeyBridge(this.logger);
            this.keyBridge = keyBridge;
            keyBridge.attachSignalling(signalling);
            await keyBridge.attachSink(e2ee.sink);
            this.detachers.push(keyBridge.on('keysChanged', () => this.recompute()));
            const livekit = this.createLivekit({
                e2ee: e2ee.options,
                roomOptions: this.deps.platform.roomOptions?.(intent),
                logger: this.logger,
            });
            this.livekit = livekit;
            this.bindLivekit(livekit, generation);
            this.signals.attachTransport({ publish: payload => livekit.publishSignal(payload) });
            await this.deps.platform.beforeConnect?.(intent);
            await livekit.connect(token.url, token.jwt);
            this.assertGeneration(generation);
            this.detachers.push(signalling.on('ringSent', eventId => this.onRingSent(roomId, intent, eventId, options.targets)));
            this.detachers.push(signalling.on('error', error => {
                this.logger.error('rtc.call.signalling_error', { code: error.code });
                void this.recoverSignalling(generation, error);
            }));
            await signalling.join({ intent, notify: options.notify, transport });
            this.assertGeneration(generation);
            await this.enableLocalMedia(livekit, {
                microphone: options.microphoneEnabled,
                camera: options.cameraEnabled,
                audioCapture: options.audioCapture,
                videoCapture: options.videoCapture,
            });
            this.assertGeneration(generation);
            this.rejoinAttempted = false;
            this.signallingRecoveries = 0;
            this.transition('joined', { joinedAt: this.clock.now() });
            this.recompute();
        }
        catch (error) {
            await this.failConnect(generation, toRtcError(error));
        }
    }
    async leave() {
        const phase = this.state.getSnapshot().phase;
        if (!isActivePhase(phase) && phase !== 'lobby') {
            return;
        }
        this.generation++;
        if (phase === 'lobby') {
            this.resetSession();
            this.state.set(initialGroupCallState);
            return;
        }
        this.transition('leaving');
        await this.teardown();
        this.transition('ended');
        this.emitter.emit('ended', 'left');
    }
    dismiss() {
        const phase = this.state.getSnapshot().phase;
        if (phase !== 'failed' && phase !== 'ended') {
            return;
        }
        this.resetSession();
        this.state.set(initialGroupCallState);
    }
    async setMicrophone(enabled, options) {
        const livekit = this.livekit;
        if (!livekit) {
            this.patchLocal({ microphoneEnabled: enabled });
            return;
        }
        await this.withDeviceOutcome('microphone', enabled, () => livekit.setMicrophone(enabled, options));
        this.recompute();
    }
    async setCamera(enabled, options) {
        const livekit = this.livekit;
        if (!livekit) {
            this.patchLocal({ cameraEnabled: enabled });
            return;
        }
        await this.withDeviceOutcome('camera', enabled, () => livekit.setCamera(enabled, options));
        this.recompute();
    }
    async setScreenShare(enabled) {
        if (!this.livekit) {
            throw new RtcError('invalid_request', 'Not connected');
        }
        await this.livekit.setScreenShare(enabled, enabled ? this.deps.platform.screenShareOptions?.() : undefined);
        this.recompute();
    }
    async switchDevice(kind, deviceId) {
        if (!this.livekit) {
            return false;
        }
        return this.livekit.switchDevice(kind, deviceId);
    }
    setMinimized(minimized) {
        this.state.update(current => (current.minimized === minimized ? current : { ...current, minimized }));
    }
    async sendReaction(reaction) {
        return this.signals.sendReaction(reaction);
    }
    async setHandRaised(up) {
        const identity = this.localIdentity();
        if (!identity) {
            return false;
        }
        const sent = await this.signals.setHand(up, identity);
        this.recompute();
        return sent;
    }
    async declineIncoming(roomId, notificationEventId, ringId) {
        const signalling = this.signalling?.roomId === roomId ? this.signalling : this.deps.signallingFactory(roomId);
        try {
            await signalling.decline(notificationEventId);
        }
        catch (error) {
            this.logger.warn('rtc.ring.decline_event_failed', { error: String(error) });
        }
        finally {
            if (signalling !== this.signalling) {
                signalling.dispose();
            }
        }
        const ring = this.deps.ring;
        if (!ring) {
            return;
        }
        try {
            const ids = ringId
                ? [ringId]
                : (await ring.findByNotification(roomId, notificationEventId))
                    .filter(result => result.status === 'RINGING')
                    .map(result => result.ringId);
            await Promise.all(ids.map(id => ring.decline(id)));
        }
        catch (error) {
            this.logger.warn('rtc.ring.decline_api_failed', { error: String(error) });
        }
    }
    dispose() {
        this.generation++;
        void this.teardown();
        this.signals.dispose();
        this.emitter.removeAll();
    }
    createLivekit(options) {
        return this.deps.livekitFactory ? this.deps.livekitFactory(options) : new LivekitController(options);
    }
    bindLivekit(livekit, generation) {
        this.detachers.push(livekit.on('changed', () => this.recompute()));
        this.detachers.push(livekit.on('connection', connection => {
            if (generation !== this.generation) {
                return;
            }
            const phase = this.state.getSnapshot().phase;
            if (connection === 'reconnecting' && phase === 'joined') {
                this.transition('reconnecting');
            }
            else if (connection === 'connected' && phase === 'reconnecting') {
                this.transition('joined');
            }
        }));
        this.detachers.push(livekit.on('disconnected', unexpected => {
            if (generation !== this.generation || !unexpected) {
                return;
            }
            if (this.state.getSnapshot().phase === 'joining') {
                void this.failConnect(generation, new RtcError('media_failed', 'Media connection could not be established'));
                return;
            }
            void this.handleUnexpectedDisconnect(generation);
        }));
        this.detachers.push(livekit.on('data', (payload, from) => this.signals.receive(payload, from)));
        this.detachers.push(livekit.on('deviceError', (error, kind) => {
            const deviceKind = deviceKindOf(kind);
            if (generation !== this.generation || !deviceKind) {
                return;
            }
            this.setDeviceIssue(deviceKind, classifyDeviceError(error));
        }));
        this.detachers.push(livekit.on('encryptionError', (_error, identity) => {
            this.logger.warn('rtc.call.encryption_error', { identity });
            this.recompute();
        }));
    }
    async recoverSignalling(generation, error) {
        const signalling = this.signalling;
        const transport = this.transport;
        const snapshot = this.state.getSnapshot();
        const inCall = snapshot.phase === 'joined' || snapshot.phase === 'reconnecting';
        if (generation !== this.generation) {
            return;
        }
        if (this.clock.now() - this.lastSignallingRecoveryAt > MEMBERSHIP_TIMING.recoveryWindowMs) {
            this.signallingRecoveries = 0;
        }
        if (error.code !== 'membership_failed' || !inCall || !signalling || !transport || this.signallingRecoveries >= MEMBERSHIP_TIMING.maxRecoveries) {
            await this.abort(error);
            return;
        }
        this.signallingRecoveries++;
        this.lastSignallingRecoveryAt = this.clock.now();
        this.logger.warn('rtc.call.signalling_recovering', { attempt: this.signallingRecoveries });
        this.transition('reconnecting');
        await new Promise(resolve => this.clock.setTimeout(resolve, MEMBERSHIP_TIMING.recoveryDelayMs));
        if (generation !== this.generation) {
            return;
        }
        try {
            await signalling.rejoin({ intent: snapshot.intent, notify: 'none', transport });
            this.assertGeneration(generation);
            this.logger.info('rtc.call.signalling_recovered', { attempt: this.signallingRecoveries });
            if (this.livekit?.connectionState !== 'reconnecting') {
                this.transition('joined');
            }
            this.recompute();
        }
        catch (rejoinError) {
            if (generation === this.generation) {
                await this.abort(toRtcError(rejoinError, 'membership_failed'));
            }
        }
    }
    async handleUnexpectedDisconnect(generation) {
        const roomId = this.state.getSnapshot().roomId;
        const signalling = this.signalling;
        const livekit = this.livekit;
        const transport = this.transport;
        if (this.rejoinAttempted || !roomId || !signalling || !livekit || !transport) {
            await this.abort(new RtcError('network', 'Media connection lost'));
            return;
        }
        this.rejoinAttempted = true;
        this.transition('reconnecting');
        try {
            const token = await this.deps.tokens.getToken({
                roomId,
                slotId: RTC_PROTOCOL_V1.slotId,
                memberId: memberIdFor(signalling.localUserId, signalling.localDeviceId),
                url: transport.livekit_service_url,
                serverName: serverNameOf(signalling.localUserId),
            });
            this.assertGeneration(generation);
            const local = this.state.getSnapshot().local;
            await livekit.connect(token.url, token.jwt);
            await this.enableLocalMedia(livekit, { microphone: local.microphoneEnabled, camera: local.cameraEnabled });
            this.assertGeneration(generation);
            this.transition('joined');
            this.recompute();
        }
        catch (error) {
            if (generation === this.generation) {
                await this.abort(toRtcError(error, 'network'));
            }
        }
    }
    async enableLocalMedia(livekit, media) {
        await this.tryLocalMedia('microphone', () => livekit.setMicrophone(media.microphone, media.audioCapture));
        if (media.camera) {
            await this.tryLocalMedia('camera', () => livekit.setCamera(true, media.videoCapture));
        }
    }
    async tryLocalMedia(kind, enable) {
        try {
            await enable();
        }
        catch (error) {
            const failure = toRtcError(error, 'media_failed');
            this.logger.warn('rtc.call.local_media_failed', { kind, code: failure.code, error: failure.message });
            this.patchLocal(kind === 'microphone' ? { microphoneEnabled: false } : { cameraEnabled: false });
            this.setDeviceIssue(kind, classifyDeviceError(error));
            this.emitter.emit('mediaFailed', { kind, error: failure });
        }
    }
    onMatrixParticipants(participants) {
        const before = this.knownIdentities;
        this.knownIdentities = new Set(participants.map(participant => participant.livekitIdentity));
        before.forEach(identity => {
            if (!this.knownIdentities.has(identity)) {
                this.signals.participantLeft(identity);
            }
        });
        const joinedSomeone = [...this.knownIdentities].some(identity => !before.has(identity));
        const current = this.state.getSnapshot();
        if (current.phase === 'lobby') {
            this.state.set({ ...current, lobbyParticipants: participants, full: this.isFull(participants) });
            return;
        }
        if (joinedSomeone) {
            void this.signals.rebroadcastHand();
        }
        this.recompute();
    }
    adoptNotification(notificationEventId) {
        this.state.update(current => (current.notificationEventId ? current : { ...current, notificationEventId }));
    }
    async ringMembers(targets, notificationEventId) {
        const ring = this.deps.ring;
        const current = this.state.getSnapshot();
        const eventId = notificationEventId ?? current.notificationEventId;
        if (!ring || !current.roomId || current.phase !== 'joined') {
            throw new RtcError('invalid_request', 'Ringing needs a joined call');
        }
        if (!eventId) {
            throw new RtcError('invalid_request', 'Ringing needs the call notification');
        }
        const requestId = (this.deps.requestIdFactory ?? randomRequestId)();
        const result = await ring.start({
            roomId: current.roomId,
            slotId: RTC_PROTOCOL_V1.slotId,
            intent: current.intent,
            notificationEventId: eventId,
            targetUserIds: targets,
            requestId,
        });
        await this.deliverRing(targets, result, current.roomId, current.intent, eventId);
        return result;
    }
    onRingSent(roomId, intent, notificationEventId, targets) {
        this.state.update(current => (current.roomId === roomId ? { ...current, notificationEventId } : current));
        const ring = this.deps.ring;
        if (!ring) {
            return;
        }
        ring
            .start({ roomId, slotId: RTC_PROTOCOL_V1.slotId, intent, notificationEventId, ...(targets ? { targetUserIds: targets } : {}) })
            .then(async (result) => {
            this.state.update(current => current.roomId === roomId
                ? {
                    ...current,
                    ring: {
                        ringId: result.ringId,
                        mode: result.mode,
                        targetCount: result.targetCount,
                        expiresAt: result.expiresAt,
                    },
                }
                : current);
            if (targets) {
                await this.deliverRing(targets, result, roomId, intent, notificationEventId);
            }
        })
            .catch(error => this.logger.warn('rtc.ring.start_failed', { error: String(error) }));
    }
    async deliverRing(targets, result, roomId, intent, notificationEventId) {
        const delivery = this.deps.ringDelivery;
        if (!delivery || result.mode !== 'RING' || targets.length === 0) {
            return;
        }
        try {
            await delivery.deliver(targets, { ringId: result.ringId, roomId, intent, notificationEventId, expiresAt: result.expiresAt });
        }
        catch (error) {
            this.logger.warn('rtc.ring.delivery_failed', { error: String(error), targets: targets.length });
        }
    }
    recompute() {
        const signalling = this.signalling;
        if (!signalling) {
            return;
        }
        const matrix = signalling.participants();
        this.knownIdentities = new Set(matrix.map(participant => participant.livekitIdentity));
        const media = this.livekit?.snapshot() ?? [];
        const keyed = this.keyBridge?.keyedIdentities() ?? new Set();
        const { views, unmapped } = mergeParticipants(matrix, media, keyed, this.signals.raisedHands());
        const localMedia = media.find(snapshot => snapshot.isLocal);
        this.state.update(current => ({
            ...current,
            participants: views,
            encryption: summarizeEncryption(views, unmapped),
            full: this.isFull(matrix),
            local: localMedia
                ? {
                    microphoneEnabled: localMedia.microphoneEnabled,
                    cameraEnabled: localMedia.cameraEnabled,
                    screenSharing: localMedia.screenSharing,
                    deviceIssues: current.local.deviceIssues,
                }
                : current.local,
        }));
    }
    async withDeviceOutcome(kind, enabled, change) {
        try {
            await change();
        }
        catch (error) {
            if (enabled) {
                this.setDeviceIssue(kind, classifyDeviceError(error));
            }
            throw error;
        }
        if (enabled) {
            this.setDeviceIssue(kind, undefined);
        }
    }
    setDeviceIssue(kind, issue) {
        this.state.update(current => {
            const deviceIssues = withDeviceIssue(current.local.deviceIssues, kind, issue);
            return deviceIssues === current.local.deviceIssues ? current : { ...current, local: { ...current.local, deviceIssues } };
        });
    }
    patchLocal(patch) {
        this.state.update(current => ({ ...current, local: { ...current.local, ...patch } }));
    }
    localIdentity() {
        return this.livekit?.localIdentity;
    }
    isFull(participants) {
        const local = participants.find(participant => participant.isLocal)?.userId;
        const others = new Set(participants.filter(participant => !participant.isLocal && participant.userId !== local).map(participant => participant.userId));
        return others.size >= this.maxParticipants;
    }
    transition(phase, patch = {}) {
        this.state.update(current => {
            if (!canTransition(current.phase, phase)) {
                this.logger.warn('rtc.call.invalid_transition', { from: current.phase, to: phase });
                return current;
            }
            return { ...current, ...patch, phase };
        });
    }
    fail(error, roomId) {
        this.state.update(current => ({
            ...current,
            roomId: roomId ?? current.roomId,
            phase: canTransition(current.phase, 'failed') ? 'failed' : current.phase,
            error: { code: error.code, message: error.message },
        }));
    }
    async failConnect(generation, error) {
        if (generation !== this.generation) {
            return;
        }
        this.generation++;
        await this.abort(await this.classifyConnectFailure(error));
    }
    async classifyConnectFailure(error) {
        if (error.code !== 'media_failed' && error.code !== 'network') {
            return error;
        }
        const probe = await (this.deps.iceProbe ?? probeIceGathering)().catch(() => undefined);
        if (!probe) {
            return error;
        }
        const blocked = isWebRtcBlocked(probe);
        this.logger.warn('rtc.ice.probe', { ...probe, blocked, failure: error.code });
        return blocked ? new RtcError('webrtc_blocked', 'The browser offered no network path for call media', { cause: error }) : error;
    }
    async abort(error) {
        this.generation++;
        this.logger.error('rtc.call.failed', { code: error.code, status: error.status });
        await this.teardown();
        this.fail(error);
        this.emitter.emit('ended', 'failed');
    }
    assertGeneration(generation) {
        if (generation !== this.generation) {
            throw new RtcError('cancelled', 'Call attempt was superseded');
        }
    }
    async teardown() {
        const signalling = this.signalling;
        const livekit = this.livekit;
        this.detachers.forEach(detach => detach());
        this.detachers = [];
        this.signals.attachTransport(undefined);
        this.keyBridge?.dispose();
        this.keyBridge = undefined;
        this.livekit = undefined;
        this.signalling = undefined;
        this.transport = undefined;
        await Promise.allSettled([
            signalling?.leave().finally(() => signalling.dispose()),
            livekit
                ?.disconnect()
                .finally(() => livekit.dispose())
                .then(() => this.deps.platform.afterDisconnect?.()),
        ]);
    }
    resetSession() {
        this.detachers.forEach(detach => detach());
        this.detachers = [];
        this.signalling?.dispose();
        this.signalling = undefined;
        this.knownIdentities = new Set();
    }
}
